import type { PriceRefreshResultDto } from '../shared/types';

const pendingStatuses = new Set(['PENDING', 'WAITING']);
const visibleUpdateStatuses = new Set(['FRESH', 'STALE_FALLBACK']);

export type PriceRefreshProgress = {
  completed: number;
  total: number;
  percent: number;
  visibleUpdates: number;
  label: string;
  detail: string;
  tone: 'active' | 'waiting' | 'complete' | 'warning';
};

export function parseRefreshResults(value?: string) {
  if (!value) return [] as PriceRefreshResultDto[];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((item): item is PriceRefreshResultDto => (
      Boolean(item) && typeof item === 'object'
      && typeof item.instrumentId === 'string'
      && typeof item.status === 'string'
    )).map((item) => ({ ...item, symbol: typeof item.symbol === 'string' ? item.symbol : item.instrumentId })) : [];
  } catch {
    return [];
  }
}

export function buildPriceRefreshProgress(
  jobStatus: string,
  results: PriceRefreshResultDto[],
): PriceRefreshProgress {
  const total = results.length;
  const completed = results.filter((result) => !pendingStatuses.has(result.status)).length;
  const visibleUpdates = results.filter((result) => visibleUpdateStatuses.has(result.status)).length;
  const percent = total ? Math.round(completed / total * 100) : 0;
  const waiting = results.some((result) => result.status === 'WAITING');
  const terminal = ['COMPLETED', 'PARTIAL', 'FAILED'].includes(jobStatus);
  const warning = terminal && jobStatus !== 'COMPLETED';

  if (!total) {
    return {
      completed, total, percent, visibleUpdates,
      label: jobStatus === 'QUEUED' ? 'กำลังจัดคิวอัปเดตราคา' : 'กำลังเตรียมรายการราคา',
      detail: 'ระบบจะอัปเดตหน้าเว็บให้อัตโนมัติเมื่อราคาใหม่เข้ามา',
      tone: 'active',
    };
  }
  if (terminal) {
    return {
      completed, total, percent: 100, visibleUpdates,
      label: warning ? `ตรวจราคาเสร็จบางส่วน ${completed}/${total} รายการ` : `ตรวจราคาเสร็จแล้ว ${completed}/${total} รายการ`,
      detail: warning ? 'บางรายการใช้ราคาเดิมหรือยังไม่มีราคา' : 'ราคาล่าสุดแสดงบนหน้าเว็บแล้ว',
      tone: warning ? 'warning' : 'complete',
    };
  }
  return {
    completed, total, percent, visibleUpdates,
    label: waiting ? `ตรวจแล้ว ${completed}/${total} รายการ` : `กำลังตรวจราคา ${completed}/${total} รายการ`,
    detail: waiting ? 'กำลังรอโควตารอบถัดไป และจะทำต่ออัตโนมัติ' : 'ราคาใหม่จะแสดงบนหน้าเว็บทันทีที่ได้รับ',
    tone: waiting ? 'waiting' : 'active',
  };
}
